import head from 'lodash/head';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { compose } from 'recompose';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import withHooks from '~/common/hoc/withHooks';
import withIntercom from '~/common/hoc/withIntercom';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import paths from '~/common/jsons/paths.json';

const SETTINGS_ITEMS_ORDER = [
  { role: 'APIKEY:READ', path: paths.api_keys },
  { role: 'RELATION:READ', path: paths.relationship },
  { role: 'REPLICA:READ', path: paths.multi_instance },
  { role: 'CAPSULE:READ', path: paths.capsule_headers },
  { role: 'CAPSULE:READ', path: paths.data_streaming }
];

const REALM_SELECTION_ROLES = ['GROUP:ADMIN', 'GROUP:SUPPORT', 'ORG:MANAGER'];
const CONNECTIVITY_ROLES = ['GROUP:ADMIN', 'ZTNA:READ'];

const enhancer = compose(
  withIntercom,
  withHooks(props => {
    const location = useLocation();
    const {
      acceptanceTerm,
      realms,
      application,
      authentication,
      acls,
      profile
    } = useSelector(state => state);
    const dispatch = useDispatch();
    const {
      MONITOR_SETDEFAULTENVIRONMENT_FRONT,
      AI_NATIVE_MONITORING,
      AI_NATIVE_PERMISSION_GATING
    } = featureFlagConstants;
    const { treatments } = useFeatureFlag([
      MONITOR_SETDEFAULTENVIRONMENT_FRONT,
      AI_NATIVE_MONITORING,
      AI_NATIVE_PERMISSION_GATING
    ]);

    const realmName = application?.realm?.realm;
    const isRunView = location.pathname.includes(`${realmName}/operation/run`);
    const isMonitorView = location.pathname.includes(
      `${realmName}/operation/monitor`
    );
    const isAiNativeMonitoringEnabled =
      treatments[AI_NATIVE_MONITORING]?.treatment === 'on';
    const isPermissionGatingEnabled =
      treatments[AI_NATIVE_PERMISSION_GATING]?.treatment === 'on';
    const canNavigateToDigitalWorker = isMonitorView
      ? isAiNativeMonitoringEnabled
      : !isRunView;
    const isBuildView = location.pathname.includes(
      `${realmName}/operation/build`
    );
    const viewByName = {
      Build: isBuildView,
      Run: isRunView,
      Monitor: isMonitorView
    };
    const modeSwitchView =
      Object.keys(viewByName).find(name => viewByName[name]) || null;

    const enviromentDefault =
      treatments[MONITOR_SETDEFAULTENVIRONMENT_FRONT].treatment;

    function getEnvriomentByFeatureFlag(allEnviroment) {
      return allEnviroment.find(env => env === enviromentDefault);
    }

    function getEnvriomentByFeatureFlagRun(allEnviroment) {
      return allEnviroment.find(env => env === 'test');
    }

    const scopes = acls?.scopes || [];

    const hasScope = role =>
      scopes.some(
        scope =>
          (scope.includes('{ENV') ? scope.split('{ENV')[0] : scope) === role
      );
    const requiredDigitalWorkerRole = isMonitorView
      ? 'LOG:READ'
      : 'PIPELINE:CREATE';

    const state = {
      allIds: realms?.result?.realms || [],
      byId: realms.entities.realms,
      activeRealm: application?.realm,
      user: authentication?.userData,
      scopes,
      environments: application?.environments?.allNames,
      eula: acceptanceTerm?.result,
      currentPath: location.pathname,
      canSelectRealm: scopes.some(scope =>
        REALM_SELECTION_ROLES.includes(scope)
      ),
      canSeeConnectivity: scopes.some(scope =>
        CONNECTIVITY_ROLES.includes(scope)
      ),
      canUseDigitalWorker:
        !isPermissionGatingEnabled ||
        hasScope('GROUP:ADMIN') ||
        hasScope(requiredDigitalWorkerRole),
      isMainRealm:
        authentication?.userData?.realm === application?.realm?.realm,
      isDigitalWorkerNavigable: canNavigateToDigitalWorker,
      modeSwitchView,
      enabledSupportAccess: application?.enabledSupportAccess?.enabled,
      userName: profile?.user
        ? `${profile.user.firstName} ${profile.user.lastName}`
        : authentication?.userData?.email
    };
    const actions = {
      setModalTwoFactor: dispatch.profile.setModalTwoFactor,
      setModalNewPassword: dispatch.profile.setModalNewPassword,
      setModalProfile: dispatch.profile.setModalProfile,
      onSignOut: dispatch.authentication.signOut,
      openSupportAccess: () => {
        dispatch.application.handleIsOpenSuportAccess();
      },
      openEula: () => {
        dispatch.eula.setModal(true);
      },
      containPaths: routePaths =>
        routePaths?.reduce((result, path) => {
          if (!result) return location.pathname.indexOf(path) > -1;

          return result;
        }, false),
      containScopes: roles => {
        const withDefault = [].concat(roles, 'GROUP:ADMIN');
        return !scopes.some(scope => withDefault.includes(scope));
      },
      goToManualMode: () => {
        dispatch.router.navigate({
          to: `/${state.activeRealm.realm}/operation/build/pipelines`
        });
      },
      goToDigitalWorkerMode: () => {
        const target =
          isMonitorView && isAiNativeMonitoringEnabled
            ? `/digital-worker-mode/${realmName}/monitoring`
            : `/digital-worker-mode/${realmName}`;
        window.location.assign(target);
      },
      pathNameRuntime: () => {
        const envsWithAcl = (acc, current) => {
          const defaultAcl = ['GROUP:ADMIN', 'DEPLOYMENT:READ'];
          const acl = `DEPLOYMENT:READ{ENV=${current.toUpperCase()}}`;
          const roles = [...defaultAcl, acl];
          const isRole = state.scopes.find(r => roles.includes(r));

          if (isRole) return [...acc, current];

          return acc;
        };
        const envsAcl = state.environments.reduce(envsWithAcl, []);

        const newEnvironmentDefault = getEnvriomentByFeatureFlagRun(envsAcl);

        if (envsAcl.length > 0 && newEnvironmentDefault)
          return head([newEnvironmentDefault]);

        return head(state.environments);
      },
      goToSettings: () => {
        const { realm } = state.activeRealm;
        const hasRole = role =>
          scopes.some(
            scope =>
              scope === role ||
              (scope.includes('{ENV') && scope.split('{ENV')[0] === role)
          );

        const firstItem = SETTINGS_ITEMS_ORDER.find(({ role }) =>
          hasRole(role)
        );
        const path = firstItem
          ? `/${realm}${firstItem.path}`
          : `/${realm}${paths.settings}`;

        dispatch.router.navigate({ to: path });
      },
      goToAdmin: () => {
        dispatch.router.navigate({
          to: `/${state.activeRealm.realm}/settings/beta/user`
        });
      },
      goToConnectivity: () => {
        dispatch.router.navigate({
          to: `/${state.activeRealm.realm}/connectivity/edge-routers`
        });
      },
      getEnvriomentByFeatureFlag
    };

    return {
      ...props,
      ...state,
      ...actions
    };
  })
);

export default enhancer;
